import {
    getUser,
    listUsers
} from './queries';
import {
    createUser,
    deleteUser,
    updateUser,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';

import {
    CreateUser,
    DeleteUser,
    GetUser,
    ListUsers,
    UpdateUser,
    User,
    UsersFilter,
} from "../types";

import {generateClient} from 'aws-amplify/api';
import {usersApiConfig} from "../config/flexinet-users-api";
import {toCreateInput, toUpdateInput} from './userInput';

// UsersFilterInput accepts only { email, sub, accountId }; anything else (the
// module's client-side `status`) and every blank value stays out of the request.
const SERVER_FILTER_KEYS = ['email', 'sub', 'accountId'] as const;

const normalizeFilter = (filter: UsersFilter) =>
    SERVER_FILTER_KEYS.reduce((acc: Record<string, string>, key) => {
        const value = filter[key];
        if (typeof value === 'string' && value.trim() !== '') {
            acc[key] = value.trim();
        }
        return acc;
    }, {});

const client = generateClient({
                                  endpoint: usersApiConfig.endpoint,
                                  authMode: 'userPool'
                              });

// One page of users. Returns the WHOLE connection: discarding nextToken here is
// what made the console's list unpageable.
export const fetchRecords = async (
    filter: UsersFilter = {},
    limit?: number,
    nextToken?: string | null,
) => {
    const normalized = normalizeFilter(filter);

    const response = await client.graphql<GraphQLQuery<ListUsers>>({
        query: listUsers,
        variables: {
            ...(Object.keys(normalized).length ? {filter: normalized} : {}),
            ...(typeof limit === 'number' ? {limit} : {}),
            ...(nextToken ? {nextToken} : {}),
        },
    });

    const items = (response.data?.listUsers?.items ?? []).filter(Boolean);
    return {
        items: items.map(item => ({...item, id: item.username || item.sub || ''})),
        nextToken: response.data?.listUsers?.nextToken ?? null,
    };
};

export const fetch = async (username: string) => {
    const response = await client.graphql<GraphQLQuery<GetUser>>({
                                                                     query: getUser,
                                                                     variables: {username: username},
                                                                 });
    const user = response.data?.getUser;
    return user ? {...user, id: user.username || user.sub || ''} : undefined;
};

// Both writes go through userInput.ts, which leaves the Account link and the
// Account Admin flag out of the input (FNP-2015). Never pass a record straight
// through: users-api writes whatever it is given.
export const add = async (record: User) => {
    const response = await client.graphql<GraphQLQuery<CreateUser>>({
                                                                        query: createUser,
                                                                        variables: {input: toCreateInput(record)},
                                                                    });
    return response.data?.createUser;
};

export const update = async (username: string, record: User) => {
    const response = await client.graphql<GraphQLQuery<UpdateUser>>({
                                                                        query: (updateUser),
                                                                        variables: {input: toUpdateInput({...record, username})},
                                                                    });
    return response.data?.updateUser;
};

export const deleteRecord = async (username: string) => {
    const response = await client.graphql<GraphQLQuery<DeleteUser>>({
                                                                        query: (deleteUser),
                                                                        variables: {input: {username: username}},
                                                                    });
    return response.data?.deleteUser;
};
